<template>
  <app-page-container :showNav="false">
    <div class="session-info-bar" v-if="statusVisible || (!userIsOnline && !leaving)" :style="loading && !leaving ? 'z-index:9999;' : ''">
      {{ statusMessage }}
    </div>
    <div class="session-info-bar" v-if="partnerStatusVisible && !leaving && userIsOnline" :style="loading && !leaving ? 'z-index:9999;' : ''">
      {{ partnerStatusMessage }}
    </div>
    <LoadingSession v-show="loading && !leaving" :loading="loading" :leaving="leaving" />
    <div v-if="ready && sessionLoaded && sessionID && user && user._id" :key="sessionID" class="room-container">
      <SessionSettings v-if="settings" @reconnect="reconnect" />
      <RoomShortcuts v-hotkey="keymap" v-show="showShortcutsModal" @wrapperClick="toggleShowShortcuts" />
      <DefaultModal v-if="completionModalOpen && !sessionCompleteConfirmed" @callModalSecondAction="confirmCompleteSession" type="SessionComplete" />
      <div class="video-container">
        <RoomSessionBar :userID="user._id" :sessionLoaded="sessionLoaded" :isCompleted="isCompleted" :timerVisible="timerVisible" />
        <div class="video-chat">
          <DuoSession :key="sessionID" :sessionID="sessionID" @rebootWithNewSessionID="rebootWithNewSessionID" />
          <FloatingChat v-if="chat && sessionLoaded" :sessionLoaded="sessionLoaded" :user="user" :isCompleted="isCompleted" />
          <RoomBottomBar @toggleMedia="toggleMedia" ref="bottomBar" />
        </div>
      </div>
    </div>
  </app-page-container>
</template>

<script>
import { mapState } from "vuex";
import DefaultModal from "../../components/DefaultModal";
import { initShortcuts } from "./Session/SessionHelpers/shortcuts";
import AppPageContainer from "@/components/AppPageContainer";
import LoadingSession from "./Session/SessionComponents/LoadingSession";
import RoomSessionBar from "./RoomComponents/RoomSessionBar";
import RoomBottomBar from "./RoomComponents/RoomBottomBar";
import RoomShortcuts from "./RoomComponents/RoomShortcuts";
import SessionSettings from "./Session/SessionComponents/SessionSettings/SessionSettings";
import { confirmCompleteSession } from "./Session/SessionHelpers/session";

import { toggleAudio, toggleVideo, toggleScreenshare, togglePictureInPicture, syncMediaStates, stopSyncingMediaStates } from "./Session/SessionHelpers/media";
import { sendChatMessage } from "./Session/SessionHelpers/chat";

import DuoSession from "./Session/DuoSession";

import { resetRoom, attemptLaunchVideo } from "./Session/SessionHelpers/Room/room";
import { getSession, getPartnerSessionCount } from "./Session/SessionHelpers/session";
import timestamps from "./Session/SessionHelpers/timestamps";

import FloatingChat from "./SessionChatComponents/FloatingChat";

import sessionSocket from "../../socket/sessionSocket";
import rematchSocket from "../../socket/rematchSocket";

import logo from "../../assets/logo.png";

import { playChatMessageSound } from "./Session/SessionHelpers/sound";
import roomAPIService from "./roomAPIService";

function initialState() {
  return {
    logo: logo,

    userIsOnline: true,

    showShortcutsModal: false,

    timerVisible: true,
  };
}

export default {
  name: "RoomPage",
  data: function () {
    return initialState();
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      isAuthenticated: (state) => state.auth.authenticationStatus,
      leftSidebar: (state) => state.toolbar.containersConfig.leftSidebar,

      settings: (state) => state.toolbar.toolbarConfig.settings,

      isCompleted: (state) => state.session.isCompleted,
      sessionID: (state) => state.session.sessionID,
      sessionDateTimeMS: (state) => state.session.dateTimeMS,
      chat: (state) => state.toolbar.toolbarConfig.chat,

      ready: (state) => state.session.ready,
      sessionLoaded: (state) => state.session.sessionLoaded,

      loading: (state) => state.roomLoading.loading,
      leaving: (state) => state.roomLoading.leaving,

      partnerMetadata: (state) => state.session.partnerMetadata,

      sessionCompleteConfirmed: (state) => state.session.sessionCompleteConfirmed,
      completionModalOpen: (state) => state.session.completionModalOpen,

      sessionChat: (state) => state.session.sessionChat,
      sessionPreferences: (state) => state.room.sessionPreferences,

      rematchRequestDoc: (state) => state.rematching.rematchRequestDoc,

      disconnected: (state) => state.sessionState.disconnected,
      reconnecting: (state) => state.sessionState.reconnecting,
      connected: (state) => state.sessionState.connected,

      statusMessage: (state) => state.sessionState.statusMessage,
      statusVisible: (state) => state.sessionState.statusVisible,

      partnerStatusMessage: (state) => state.sessionState.partnerStatusMessage,
      partnerStatusVisible: (state) => state.sessionState.partnerStatusVisible,
    }),
    keymap() {
      // https://github.com/Dafrok/v-hotkey
      return {
        "alt+h": this.toggleShowShortcuts,
        "alt+x": this.toggleChat,
        "alt+s": this.toggleSettings,
        "alt+p": this.togglePictureInPicture,
        "alt+f": this.toggleMirror,
        "alt+c": this.toggleCamera,
        "alt+z": this.toggleScreenshare,
        "alt+t": this.toggleTimer,
        "alt+r": this.toggleReport,
        "shift+t": this.toggleTimerVisibility,
      };
    },
  },
  components: {
    AppPageContainer,
    LoadingSession,
    RoomBottomBar,
    RoomShortcuts,
    FloatingChat,
    RoomSessionBar,
    DuoSession,
    SessionSettings,
    DefaultModal,
  },
  async created() {
    await this.initializeSession();
  },

  async beforeRouteLeave(to, from, next) {
    this.cleanBeforeLeave(true, next);
  },

  async beforeDestroy() {
    this.cleanBeforeLeave();
  },

  methods: {
    resetData: function () {
      Object.assign(this.$data, initialState());
    },
    toggleTimerVisibility() {
      if (document.activeElement === document.getElementById("chatInputBox")) {
        /** If typing in chat, don't activate toggle. */
        return;
      }
      this.timerVisible = !this.timerVisible;
    },
    async cleanBeforeLeave(fromBeforeLeave = false, next = null, fromRequestRematch = false, fromCalendarUpdate = false) {
      stopSyncingMediaStates();
      this.$store.dispatch("roomLoading/setLeavingState", true);
      this.$store.dispatch("roomLoading/setLoadingState", true);

      const userID = this.user._id;
      const sessionID = this.sessionID;
      const dateTimeMS = this.sessionDateTimeMS;

      /* ====== CANCEL ANY REMATCH REQUESTS BEFORE EXITING ====== */
      if (this.rematchRequestDoc) {
        /**
         * Canceling rematch request is done over HTTP because
         * socket gets disconnected before as the page is left,
         * however the browser still retains enough context to
         * send an HTTP request. Also cannot be an async-await
         * or it will block the rest of this from executing.
         */
        roomAPIService.cancelOneRematchRequestOverHTTP(sessionID, userID);
      }
      /* Reset all Room related Vuex data, stop all media tracks and disconnect the user from the room */
      resetRoom();

      /* Clear all Vuex data meant to be cleared only during exiting the room */
      // exitRoom();
      this.$store.dispatch("session/resetSession");

      /* Reset session connectivity states */
      this.$store.dispatch("sessionState/reset");

      /* Exit socket  */
      sessionSocket.exitSessionRooms(sessionID, dateTimeMS);
      timestamps.registerLeaveTimestampOverHTTP(sessionID, userID, dateTimeMS);

      /* In the case it was through a rematch request, the firstRematch value
      should remain as true, as it is a vital part of keeping the rematch
      modal closed when it should be, however in all other cases, it
      should be closed */
      if (!fromRequestRematch && !fromCalendarUpdate) {
        /* NB!!!MUST HAPPEN AFTER CHECKING IF ANY REMATCH REQUEST NEEDS CANCELING */
        this.$store.dispatch("rematching/reset");
      } else {
        this.$store.dispatch("rematching/setRematchRequestDoc", null);
        this.$store.dispatch("rematching/setNewPartner", null);
      }
      /* Clean reboot socket rematch pool */
      rematchSocket.exitRematchSocket();

      this.$store.dispatch("modals/resetState");

      /* Remove active event listeners */
      window.removeEventListener("online", this.setOnline);
      window.removeEventListener("offline", this.setOffline);
      window.removeEventListener("keyup", this.handler);
      var pageLeaveEvent = this.isOnIOS ? "pagehide" : "beforeunload";
      window.removeEventListener(pageLeaveEvent, this.cleanBeforeLeave);

      /* Reset local file data */
      this.resetData();

      if (fromBeforeLeave && next !== null) {
        next();
      }
    },
    async confirmCompleteSession() {
      await confirmCompleteSession();
    },

    /* Reconnect just the video call, without reloading entire session
    which means to just reset and restart the Room. */
    async attemptReconnectSession() {
      // can display top ribbon, that you are reconnecting, with possible issues
      await attemptLaunchVideo();
    },

    async rebootWithNewSessionID(newID, fromCalendarUpdate = false) {
      /* Clean previous session data before initializing new session. */
      /* await this.cleanBeforeLeave(false, null, fromRequestRematch, fromCalendarUpdate); */

      let origin = window.location.origin;
      let url = origin + `/session/${newID}`;
      window.location.href = url;
      console.log(fromCalendarUpdate);
      /* let fromRequestRematch = !fromCalendarUpdate; */

      /* Change session to new route */
      /* this.$router.push(`/session/${newID}`); */

      /* Re-initialize entire session. This still runs because it's Vue, e.g. not everything gets rerendered, and without it things break */
      /* this.initializeSession(); */
      /**
       * When using this.$router.push, when partner
       * cancels, and then it changes the session,
       * and then you exit the session, the session light
       * is still on; and it seems many things are not
       * working correct, so refreshing is safest.
       * Likewise, when the session partner changes,
       * chat and various other things seemed to break.
       * So it needs to be a * complete * reload to the
       * extent possible, or just do a full refresh.
       * Do not risk it. Smooth, flawless, working app > fancy solutions.
       * User experience is 100X more important than code elegance.
       * Ideal is both, but if only one can be chosen, always go for UX
       * over anything else —— rather go overboard with an uglier solution,
       * that you know works than gamble with something not understood.
       * Only do fancy solutions, when absolutely understand what is happening.
       */
      // window.location = `${window.location.origin}/session/${newID}`;
    },
    async initializeSession() {
      this.$store.dispatch("roomLoading/setLeavingState", false);
      const sessionID = this.$route.params.sessionID;

      /* Get and set up session — this is the core setup function */
      await getSession(sessionID);

      /** Initialize sockets. */
      await sessionSocket.initSessionSocket();
      await rematchSocket.initRematchSocket();

      this.$nextTick(function () {});
      /** @MUST be called AFTER socket is open as it uses socket. */
      await attemptLaunchVideo(this.sessionID);

      const self = this;
      this.handler = function (e) {
        self.keyboardEvent(e);
      };
      window.addEventListener("keyup", this.handler);

      if (navigator && navigator.userAgent) {
        this.isOnIOS = navigator.userAgent.match(/iPad/i) || navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPod/i);
      }
      var pageLeaveEvent = this.isOnIOS ? "pagehide" : "beforeunload";
      window.addEventListener(pageLeaveEvent, this.cleanBeforeLeave); /* helps detect refresh */

      window.addEventListener("online", this.setOnline);
      window.addEventListener("offline", this.setOffline);

      /** Re-sync states such as BRB */
      syncMediaStates();
    },
    async setOnline() {
      this.userIsOnline = true;
      /* await this.$store.dispatch("sessionState/setStatusMessage", "connected"); */
    },
    async setOffline() {
      this.userIsOnline = false;
      await this.$store.dispatch("sessionState/setStatusMessage", "disconnected");
    },
    async reconnect() {
      await this.cleanBeforeLeave();
      await this.initializeSession();
    },
    /** FOR VUE-HOT-KEYS */
    async sendChatMessage() {
      await sendChatMessage();
    },
    toggleChat() {
      this.$store.dispatch("toolbar/toggleChat");
    },
    toggleMirror() {
      this.$store.dispatch("preferences/toggleMirror");
    },
    toggleCamera() {
      this.toggleMedia(0);
    },
    toggleScreenshare() {
      this.toggleMedia(2);
    },
    togglePictureInPicture() {
      this.toggleMedia(3);
    },
    toggleSettings() {
      this.$store.dispatch("toolbar/toggleToolbar", "settings");
    },
    toggleTimer() {
      this.$store.dispatch("toolbar/toggleTimer");
    },
    toggleReport() {
      this.$store.dispatch("modals/toggleState", "reportModal");
    },
    toggleShowShortcuts() {
      this.showShortcutsModal = !this.showShortcutsModal;
    },
    /***************************************
     * MEDIA TOGGLES
     ***************************************/
    async toggleMedia(type) {
      if (type == 0) {
        await toggleVideo();
      } else if (type == 1) {
        await toggleAudio();
      } else if (type == 2) {
        await toggleScreenshare();
      } else if (type == 3) {
        togglePictureInPicture();
      }
    },

    keyboardEvent(e) {
      initShortcuts(e);
    },
  },
  watch: {
    /* For some reason, sometimes partner session id was 0, then not e.g. it wasn't set */
    partnerMetadata: {
      async handler() {
        if (this.partnerMetadata && this.partnerMetadata.ID) {
          /* Get and set partner session count */
          await getPartnerSessionCount(this.partnerMetadata.ID);
        }
      },
      deep: true,
    },
    sessionChat: function () {
      this.$nextTick(() => {
        /** If tab/window not in focus or chat not open, play chat message sound. */
        const chatIsOpen = this.chat;
        const chatSoundOn = this.sessionPreferences ? this.sessionPreferences.chatSound : true; // whether chat sound preference is turned on
        if ((!document.hasFocus() || !chatIsOpen) && chatSoundOn && this.sessionChat.length) {
          playChatMessageSound();
        }
      });
    },
    sessionID: function (newID, oldID) {
      console.log("WATCH: sessionID changed", newID, oldID, this.$route.params.sessionID);
      /** @todo: Inform user their session partner has left and they can be rematched before switching session */
      if (newID && newID !== this.$route.params.sessionID) {
        const fromCalendarUpdate = true;
        this.rebootWithNewSessionID(newID, fromCalendarUpdate);
      }
    },
  },
};
</script>

<style scoped>
:root {
  --nav-height: 44px;
  --nav-margin-top: 18px;
}
.video-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.room-container {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
  /* justify-content: center; */
  align-items: center;
  background-color: #fafafb;
  background-color: #fff;
  position: relative;
}

.video-chat {
  /* height: calc(100% - var(--nav-height) - var(--nav-margin-top)); */
  height: 100%;
  margin: 0 auto;
  width: 100%;
  position: relative;
  display: flex;
}

.room-pass-verif {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.enter-room-password {
  width: 699px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px 25px 20px;
  background-color: #f7f8f9;
  margin-bottom: 100px;
  margin-top: 25px;
  border-radius: 35px;
  padding-bottom: 40px;
}

.enter-room-password-title {
  font-size: 34px;
  font-weight: 600;
  text-align: center;
  margin-right: auto;
  margin-left: auto;
  margin-bottom: 35px;
  width: 600px;
  line-height: 40px;
}

.room-password {
  border: 1px solid #eee;
  border-radius: 10px;
  width: 300px;
  caret-color: #666;
  padding: 8px 14px;
  font-size: 22px;
  font-family: "Nunito", sans-serif;
  transition: 0.2s ease;
  box-sizing: border-box;
  outline: none;
}

.room-password:hover {
  border-color: #ccc;
}

.room-password:focus {
  border-color: #ccc;
}

.check-pass {
  font-size: 25px;
  margin-top: 15px;
  letter-spacing: 0.5px;
  background-color: #e9eced;
  color: #5600ff;
  padding: 6px 14px;
  font-weight: 700;
  border-radius: 360px;
  cursor: pointer;
  transition: 0.1s ease;
  width: 300px;
  text-align: center;
  box-sizing: border-box;
}

.check-pass:hover {
  background-color: #b7bcc194;
}

.pass-room-name {
  text-transform: capitalize;
  color: #5600ff;
}

.logo {
  height: 50px;
  width: 50px;
}

.logo-link {
  background-color: #e9eced;
}

.inputErrorContainer {
  padding: 8px 0;
  background-color: #e9eced;
  border-radius: 360px;
  margin-bottom: 0px;
  margin-top: 6px;
  margin-left: auto;
  margin-right: auto;
  color: #9a2442;
  font-weight: 700;
  box-sizing: border-box;
  width: 300px;
  text-align: center;
}

.session-info-bar {
  position: absolute;
  top: 0;
  height: 42px;
  left: 0;
  z-index: 9;
  text-align: center;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #000000d9;
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  color: white;
}
</style>
